// Tabs/buildSessionTabs.ts
import type { RegistrationSessionModelView } from '@actschurch/ccms-sdk';

export type SessionTabEntry =
  | { kind: 'special'; key: string; title: string; session: RegistrationSessionModelView }
  | { kind: 'normal'; key: 'sessions'; title: 'Sessions'; sessions: RegistrationSessionModelView[] };

/**
 * Groups a registration's sessions into tabs — a Dutch-flag-style
 * three-way partition around orderNo:
 * - Special sessions each keep their own position/tab (like the "red"/"blue"
 *   groups, ordered on either side).
 * - Normal sessions merge into a single "Sessions" tab (the "white" group) —
 *   as long as everything is sorted correctly first, that merged block lands
 *   exactly where its orderNo range places it, with no extra logic needed.
 *
 * ⚠️ `RegistrationSessionModelView.orderNo` (SDK 0.0.54) now carries the display order directly, so
 * the row is the source of truth. This used to depend entirely on a `courseSessionID -> orderNo` map
 * built from a **second** request to the course's session catalogue, and any session missing from that
 * map sorted to the very end with a console warning.
 *
 * The map is still accepted as a **fallback** for rows whose `orderNo` is absent — the field is
 * non-optional in the type but older payloads may not populate it, and silently sorting a real session
 * last is worse than consulting the map we already have. Once every row carries it the parameter and
 * the second request can both go.
 */
export function buildSessionTabs(
  sessions: RegistrationSessionModelView[],
  orderNoByCourseSessionID: Map<number, number> = new Map()
): SessionTabEntry[] {
  const MISSING_ORDER = Number.MAX_SAFE_INTEGER;

  const getOrderNo = (session: RegistrationSessionModelView): number => {
    // The row's own order wins. `0` is legitimate (ad-hoc sessions), so this checks for a real
    // number rather than truthiness.
    if (typeof session.orderNo === 'number' && Number.isFinite(session.orderNo)) {
      return session.orderNo;
    }

    const value = orderNoByCourseSessionID.get(Number(session.courseSessionID));
    if (value === undefined) {
      console.warn(
        `[buildSessionTabs] No orderNo on the session or in the catalogue for ` +
        `courseSessionID=${session.courseSessionID} (session "${session.name}", ` +
        `registrationSessionID=${session.registrationSessionID}). Sorting it last.`
      );
      return MISSING_ORDER;
    }
    return value;
  };

  const sorted = sessions
    .map((session, index) => ({ session, index, orderNo: getOrderNo(session) }))
    .sort((a, b) => a.orderNo - b.orderNo || a.index - b.index) // stable tiebreak by original position
    .map((entry) => entry.session);

  const tabs: SessionTabEntry[] = [];
  let normalTab: Extract<SessionTabEntry, { kind: 'normal' }> | null = null;

  for (const session of sorted) {
    if (session.isSpecial) {
      tabs.push({
        kind: 'special',
        key: `special-${session.registrationSessionID}`,
        title: session.name ?? `Session ${session.registrationSessionID}`,
        session,
      });
    } else {
      if (!normalTab) {
        normalTab = { kind: 'normal', key: 'sessions', title: 'Sessions', sessions: [] };
        tabs.push(normalTab);
      }
      normalTab.sessions.push(session);
    }
  }

  return tabs;
}